import { forwardRef, useEffect, useState } from 'react';

import { Box, Popper, useForkRef } from '@mui/material';
import { Input, useAutocomplete } from '@mui/base';
import { SxPropsTypes } from 'theme/MuiThemeProvider/types';

import { styles } from './styles';
import { BaseInput } from '../BaseInput';

type Choice = {
  id: number;
  name: string;
};
export interface AutocompleteInputProps {
  label: string;
  field: string;
  value: number | undefined;
  description?: string;
  full?: boolean;
  initialValue?: string;
  choices: Choice[];
  choiceLimit?: number;
  containerSx?: SxPropsTypes;
  inputSx?: SxPropsTypes;
  optionSx?: SxPropsTypes;
  noResultsSx?: SxPropsTypes;
  onChange: (field: string, value: string | number) => void;
  freeSolo?: boolean;
  placeholder?: string;
}

export const AutocompleteInput = forwardRef<HTMLDivElement, AutocompleteInputProps>(
  (
    {
      label,
      field,
      value,
      choices,
      onChange,
      freeSolo,
      description,
      choiceLimit,
      placeholder,
      initialValue = '',
      containerSx = {},
      inputSx = {},
      optionSx = {},
      noResultsSx = {},
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState<string>(initialValue);
    useEffect(() => {
      const crop = value ? choices.find((c) => c.id === value) : undefined;
      if (crop?.name) setInputValue(crop?.name);
    }, [choices, value]);
    const {
      getRootProps,
      getInputProps,
      getListboxProps,
      getOptionProps,
      groupedOptions,
      popupOpen,
      anchorEl,
      setAnchorEl,
    } = useAutocomplete({
      inputValue,
      onInputChange: (_, newInputValue, reason) => {
        if (reason !== 'reset') setInputValue(newInputValue);
        if (freeSolo) onChange(field, newInputValue);
      },
      options: choices,
      getOptionLabel: (option) => (typeof option !== 'string' ? option.name : option),
      onChange: (_, newValue) => {
        if (typeof newValue !== 'string' && newValue?.id) onChange(field, newValue.id);
      },
      freeSolo,
    });
    const rootRef = useForkRef(ref, setAnchorEl);
    return (
      <BaseInput sx={containerSx} label={label} description={description}>
        <Box sx={{ ...styles.inputContainer }} {...getRootProps()} ref={rootRef} className="Autocomplete__root">
          <Input
            ref={setAnchorEl}
            placeholder={placeholder}
            slotProps={{
              root: {
                className: 'Autocomplete__input-root',
              },
              input: {
                className: 'Autocomplete__input',
                style: { ...inputSx },
                ...getInputProps(),
              },
            }}
          />
          {anchorEl ? (
            <Popper
              disablePortal
              open={popupOpen}
              anchorEl={anchorEl}
              sx={styles.popper}
              slotProps={{
                root: { className: 'Autocomplete__popper' },
              }}
              placement="bottom-start"
              modifiers={[
                { name: 'flip', enabled: false },
                { name: 'preventOverflow', enabled: false },
              ]}
            >
              <Box component="ul" {...getListboxProps()} className="Autocomplete__listbox" sx={styles.list}>
                {(groupedOptions as Choice[]).slice(0, choiceLimit || groupedOptions.length).map((option, index) => {
                  const { key, ...optionProps } = getOptionProps({ option, index });
                  return (
                    <Box
                      key={key}
                      component="li"
                      {...optionProps}
                      className="Autocomplete__option"
                      sx={{ ...styles.listItem, ...optionSx }}
                    >
                      {option.name}
                    </Box>
                  );
                })}

                {groupedOptions.length === 0 && (
                  <Box component="li" sx={{ ...styles.noResults, ...noResultsSx }} className="Autocomplete__no-options">
                    No results
                  </Box>
                )}
              </Box>
            </Popper>
          ) : null}
        </Box>
      </BaseInput>
    );
  },
);
